/**
 * StructuredData — per-route JSON-LD injector.
 *
 * Renders a `<script type="application/ld+json">` node directly into
 * <head> and cleans up on unmount so route changes don't accumulate
 * stale schema. No dependency on react-helmet — one small custom hook.
 *
 * Usage:
 *   <StructuredData id="faq-page" json={FAQ_JSON_LD} />
 *
 * The `id` is required so a re-render replaces the previous node rather
 * than appending a duplicate. The two site-wide blocks (Organization +
 * WebSite) live in `/public/index.html` and are always present; this
 * component adds per-page ones (FAQPage, SoftwareApplication, Article, …).
 */
import { useEffect } from "react";

export default function StructuredData({ id, json }) {
    useEffect(() => {
        if (!id || !json) return undefined;
        const domId = `ldjson-${id}`;
        let el = document.getElementById(domId);
        if (!el) {
            el = document.createElement("script");
            el.type = "application/ld+json";
            el.id = domId;
            document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(json);
        return () => {
            const stale = document.getElementById(domId);
            if (stale?.parentNode) stale.parentNode.removeChild(stale);
        };
    }, [id, json]);
    return null;
}
